import { create } from "zustand";
import { produce } from "immer";
import { notifications } from "@mantine/notifications";
import {
  fetchBrandMonitors,
  fetchBrandMonitorById,
  createBrandMonitor,
  updateBrandMonitor,
  deleteBrandMonitor,
  fetchMonitorResultDetails,
} from "./monitorsApi";
import i18n from "../i18n";

export const useMonitorsStore = create((set, get) => ({
  // State
  monitorsByBrand: {}, // { brandId: [monitors] }
  monitorResults: {}, // { monitorId: { resultId: resultData } }
  monitorResultDetails: {}, // { `${monitorId}_${resultId}`: details }
  isLoading: false,
  isLoadingResults: false,

  // Monitor CRUD operations
  fetchMonitorsByBrandId: async (brandId) => {
    if (!brandId || brandId === "undefined") {
      set({ isLoading: false });
      return [];
    }
    // const existingMonitors = get().monitorsByBrand[brandId];
    // if (existingMonitors) {
    //   console.log(
    //     "Monitors found in store for brand:",
    //     brandId,
    //     existingMonitors
    //   );
    //   return existingMonitors;
    // }

    // console.log("Monitors not found in store, fetching for brand:", brandId);
    set({ isLoading: true });
    try {
      const monitors = await fetchBrandMonitors(brandId);
      set(
        produce((state) => {
          state.monitorsByBrand[brandId] = monitors;
          state.isLoading = false;
        })
      );
      return monitors;
    } catch (err) {
      console.error("Error fetching monitors:", err);
      notifications.show({
        title: i18n.t(
          "monitors.notifications.fetch_failed",
          "Failed to fetch monitors"
        ),
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  getMonitorById: async (brandId, monitorId) => {
    const monitors = get().monitorsByBrand[brandId];
    const existingMonitor = monitors?.find((m) => m.id === monitorId);
    if (existingMonitor) {
      console.log("Monitor found in store:", existingMonitor);
      return existingMonitor;
    }

    console.log("Monitor not found in store, fetching:", brandId, monitorId);
    set({ isLoading: true });
    try {
      const monitor = await fetchBrandMonitorById(brandId, monitorId);
      set(
        produce((state) => {
          if (!state.monitorsByBrand[brandId]) {
            state.monitorsByBrand[brandId] = [];
          }
          // Update existing monitor or add new one
          const existingIndex = state.monitorsByBrand[brandId].findIndex(
            (m) => m.id === monitorId
          );
          if (existingIndex >= 0) {
            state.monitorsByBrand[brandId][existingIndex] = monitor;
          } else {
            state.monitorsByBrand[brandId].push(monitor);
          }
          state.isLoading = false;
        })
      );
      return monitor;
    } catch (err) {
      console.error("Error fetching monitor:", err);
      notifications.show({
        title: i18n.t(
          "monitors.notifications.fetch_failed",
          "Failed to fetch monitor"
        ),
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  createMonitor: async (brandId, monitorData) => {
    set({ isLoading: true });
    try {
      const newMonitor = await createBrandMonitor(brandId, monitorData);
      set(
        produce((state) => {
          if (!state.monitorsByBrand[brandId]) {
            state.monitorsByBrand[brandId] = [];
          }
          state.monitorsByBrand[brandId].push(newMonitor);
          state.isLoading = false;
        })
      );
      notifications.show({
        title: i18n.t(
          "monitors.notifications.created",
          "Monitor created successfully"
        ),
        color: "green",
      });
      return newMonitor;
    } catch (err) {
      console.error("Error creating monitor:", err);
      notifications.show({
        title: i18n.t(
          "monitors.notifications.create_failed",
          "Failed to create monitor"
        ),
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  updateMonitor: async (brandId, monitorId, monitorData) => {
    set({ isLoading: true });
    try {
      const updatedMonitor = await updateBrandMonitor(
        brandId,
        monitorId,
        monitorData
      );
      set(
        produce((state) => {
          if (state.monitorsByBrand[brandId]) {
            const index = state.monitorsByBrand[brandId].findIndex(
              (m) => m.id === monitorId
            );
            if (index >= 0) {
              state.monitorsByBrand[brandId][index] = updatedMonitor;
            }
          }
          state.isLoading = false;
        })
      );
      notifications.show({
        title: i18n.t(
          "monitors.notifications.updated",
          "Monitor updated successfully"
        ),
        color: "green",
      });
      return updatedMonitor;
    } catch (err) {
      console.error("Error updating monitor:", err);
      notifications.show({
        title: i18n.t(
          "monitors.notifications.update_failed",
          "Failed to update monitor"
        ),
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  deleteMonitor: async (brandId, monitorId) => {
    set({ isLoading: true });
    try {
      await deleteBrandMonitor(brandId, monitorId);
      set(
        produce((state) => {
          if (state.monitorsByBrand[brandId]) {
            state.monitorsByBrand[brandId] = state.monitorsByBrand[
              brandId
            ].filter((m) => m.id !== monitorId);
          }
          // Clean up related data
          delete state.monitorResults[monitorId];
          Object.keys(state.monitorResultDetails).forEach((key) => {
            if (key.startsWith(`${monitorId}_`)) {
              delete state.monitorResultDetails[key];
            }
          });
          state.isLoading = false;
        })
      );
      notifications.show({
        title: i18n.t(
          "monitors.notifications.deleted",
          "Monitor deleted successfully"
        ),
        color: "green",
      });
    } catch (err) {
      console.error("Error deleting monitor:", err);
      notifications.show({
        title: i18n.t(
          "monitors.notifications.delete_failed",
          "Failed to delete monitor"
        ),
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  // Monitor results operations
  fetchMonitorResultDetails: async (brandId, monitorId, resultId) => {
    const cacheKey = `${monitorId}_${resultId}`;
    const existingDetails = get().monitorResultDetails[cacheKey];
    if (existingDetails) {
      console.log("Monitor result details found in store:", existingDetails);
      return existingDetails;
    }

    console.log(
      "Monitor result details not found in store, fetching:",
      brandId,
      monitorId,
      resultId
    );
    set({ isLoadingResults: true });
    try {
      const details = await fetchMonitorResultDetails(
        brandId,
        monitorId,
        resultId
      );
      set(
        produce((state) => {
          state.monitorResultDetails[cacheKey] = details;
          state.isLoadingResults = false;
        })
      );
      return details;
    } catch (err) {
      console.error("Error fetching monitor result details:", err);
      notifications.show({
        title: i18n.t(
          "monitors.notifications.result_fetch_failed",
          "Failed to fetch monitor result details"
        ),
        color: "red",
      });
      set({ isLoadingResults: false });
      throw err;
    }
  },

  // Helper methods
  getMonitorsByBrandId: (brandId) => {
    return get().monitorsByBrand[brandId] || [];
  },

  clearMonitorsForBrand: (brandId) => {
    set(
      produce((state) => {
        delete state.monitorsByBrand[brandId];
        // Clean up related results
        Object.keys(state.monitorResults).forEach((monitorId) => {
          const monitors = state.monitorsByBrand[brandId] || [];
          if (!monitors.find((m) => m.id === monitorId)) {
            delete state.monitorResults[monitorId];
          }
        });
        Object.keys(state.monitorResultDetails).forEach((key) => {
          const [monitorId] = key.split("_");
          const monitors = state.monitorsByBrand[brandId] || [];
          if (!monitors.find((m) => m.id === monitorId)) {
            delete state.monitorResultDetails[key];
          }
        });
      })
    );
  },

  clearAllData: () => {
    set({
      monitorsByBrand: {},
      monitorResults: {},
      monitorResultDetails: {},
      isLoading: false,
      isLoadingResults: false,
    });
  },
}));
